import { FactorCardType, StrategiesOption, KeyCardType, Faq } from 'sections/Resources/DemurrageAndDetention/types';

export const factorCardsData: FactorCardType[] = [
  {
    id: 1,
    heading: 'Customs delays',
    description: 'Customs clearance procedures are often complex. Any incorrect or incomplete information in shipping documents can lead to delays in processing, directly impacting the time containers remain idle and accrue charges. Typos might seem small, but even they can hold up processing. To avoid this, check out the US Customs and Border Protection’s Tips for New Importers and Exporters.',
  },
  {
    id: 2,
    heading: 'Documentation Errors',
    description: 'Errors in Bills of Lading, Letters of Credit, or other important trade documents create bottlenecks and result in hefty Demurrage and Detention fees. It is imperative to ensure all documentation is complete, accurate, and submitted on time to prevent unnecessary holdups.',
  },
  {
    id: 3,
    heading: 'Port Congestion',
    description: 'Ports worldwide often experience periods of high traffic and congestion, causing delays in unloading containers. Factors like inclement weather, increased cargo volume, labor disputes, and limited terminal space can all exacerbate the issue.',
  },
  {
    id: 4,
    heading: 'Lack of Communication',
    description: 'The shipping industry involves multiple players – carriers, freight forwarders, consignees, and trucking companies. Effective and timely communication among all stakeholders is vital. A lapse in communication can cause delays, resulting in extra fees.',
  },
  {
    id: 5,
    heading: 'Inadequate Infrastructure',
    description: 'Lack of proper infrastructure at ports, rail ramps, and warehouses is a recipe for delay. Insufficient storage space, limited handling equipment, and inefficient processes contribute to cargo staying put longer than anticipated, accruing charges.',
  },
];

export const strategiesOptions: StrategiesOption[] = [
  {
    id: 1,
    heading: 'Optimise Documentation',
    description: 'Review all documentation carefully for errors, omissions, and inconsistencies before submission to prevent delays.',
  },
  {
    id: 2,
    heading: 'Embrace Technology',
    description: 'In today\'s digital age, numerous tools can make managing shipments easier. Utilising transportation management systems (TMS) provides real-time visibility into your shipments, allowing you to monitor their status and proactively respond to potential issues. Features like automated alerts, digital document management, and tracking systems contribute towards fewer errors and smoother operations, directly impacting Demurrage and Detention charges.',
  },
  {
    id: 3,
    heading: 'Plan Ahead and Collaborate',
    description: 'Effective communication is vital to navigating the logistics maze successfully. Stay in constant communication with carriers and shipping lines to understand their schedules, potential delays, and free time allowances. Work closely with suppliers to have goods prepared in a timely manner and with customs brokers to ensure all documentation is ready.',
  },
  {
    id: 4,
    heading: 'Choose the Right Partners',
    description: 'Align with reliable drayage providers to avoid logistical hiccups when transporting containers to and from ports. A proactive provider often offers pre-pull services where containers are pulled in advance to a nearby location, ensuring cargo is ready to go as soon as possible. Partnering with a warehousing and distribution company offering facilities located strategically near ports allows you to store your cargo cost-effectively should unexpected delays arise.',
  },
  {
    id: 5,
    heading: 'Consider SOC Containers',
    description: 'Shipper Owned Containers (SOC) give you greater control over your logistics operations, potentially reducing your reliance on shipping lines and their strict timelines. You become responsible for positioning and repositioning containers, offering flexibility but requiring careful planning.',
  },
];

export const keyCardsData: KeyCardType[] = [
  {
    id: 1,
    heading: 'Invoicing Requirements',
    description: 'Invoices must be issued within 30 days after charges stop accruing. They must include detailed information such as container availability, free days, and applicable rates to ensure transparency and accurate auditing.',
  },
  {
    id: 2,
    heading: 'Disputing Charges',
    description: 'Billed parties (shippers and consignees) must raise any disputes within 30 days of receiving the invoice.',
  },
  {
    id: 3,
    heading: 'Resolving Disagreements',
    description: 'The billing party must address and settle disputes within 30 days. This period can be extended with joint consent.',
  },
];

export const faqItems: Faq[] = [
  {
    id: '1',
    question: 'What is the difference between demurrage and detention?',
    answer: (cn: Function) => (
      `
        <p class='${cn('faq__answer-paragraph')}'>
          Demurrage charges apply when you leave a container at the terminal (like a port) beyond the allotted free time, while Detention fees occur when a container is held off-terminal (at your warehouse, for instance) beyond the allowed free time.
        </p>
      `
    ),
  },
  {
    id: '2',
    question: 'Who is responsible for paying Demurrage and Detention?',
    answer: (cn: Function) => (
      `
        <p class='${cn('faq__answer-paragraph')}'>
          Typically, the party responsible for the delay that results in these charges ends up paying. It could be the shipper, the consignee, or sometimes, even a third-party logistics provider. Reviewing your contract agreements is essential for clarifying responsibility.
        </p>
      `
    ),
  },
  {
    id: '3',
    question: 'What is the difference between per diem and detention?',
    answer: (cn: Function) => (
      `
        <p class='${cn('faq__answer-paragraph')}'>
          "Per Diem" literally translates to "per day" and is often used interchangeably with "Detention," both referring to the daily charge for using the container beyond the free time. However, while both terms can apply to exceeding the free time, "per diem" might specifically denote a flat daily charge. In contrast, "Detention" could refer to a charge calculated using a tiered system (increasing daily rates for extended periods). Always clarify the fee structure with your carrier.
        </p>
      `
    ),
  },
  {
    id: '4',
    question: 'How do you calculate demurrage and detention?',
    answer: (cn: Function) => (
      `
        <p class='${cn('faq__answer-paragraph')}'>
          These charges vary greatly depending on the carrier, the location (port or terminal), the type of container (size, type), and, importantly, the number of days exceeding the free time. Typically, the fee structure is outlined in the carrier\'s tariff or a service agreement with an NVOCC.
        </p>
      `
    ),
  },
  {
    id: '5',
    question: 'What are the primary objectives of the OSRA 22 final rule implemented in 2024?',
    answer: (cn: Function) => (
      `
        <p class='${cn('faq__answer-paragraph')}'>
          The primary objectives of the OSRA 22 final rule are to stimulate containerized exports, protect U.S. exporters\' interests, and ensure greater control and regulation of containerized traffic by shipping lines operating in U.S. ports. The rule aims to create consistent billing procedures, reduce unjust detention and demurrage charges, improve data visibility for better validation and dispute resolution, and ultimately reduce inflation by relieving container terminal congestion.
        </p>
      `
    ),
  },
  {
    id: '6',
    question: 'What are the key invoicing requirements under the OSRA 22 final rule?',
    answer: (cn: Function) => (
      `
        <p class='${cn('faq__answer-paragraph')}'>
          Under the OSRA 22 final rule, invoices must be issued within 30 days after charges stop accruing. They need to include detailed information such as container availability, free days, and applicable rates to guarantee transparency and allow for accurate auditing. This helps ensure that all billing processes are clear and fair for all parties involved.
        </p>
      `
    ),
  },
  {
    id: '7',
    question: 'How does the OSRA 22 final rule impact the dispute resolution process for demurrage and detention charges?',
    answer: (cn: Function) => (
      `
        <p class='${cn('faq__answer-paragraph')}'>
          The OSRA 22 final rule mandates that disputes regarding demurrage and detention charges must be raised by the billed parties (shippers and consignees) within 30 days of receiving the invoice. The billing party is then required to address and settle the disputes within 30 days, although this period can be extended if both parties consent. This structured approach aims to resolve disagreements promptly and fairly, ensuring a more transparent and efficient billing process.
        </p>
      `
    ),
  },
];